import AppSettings from '../AppSettings';
// Services
import { showLoadingToast, showToast } from '../utils/ToastsUtil';
// Librairies
import Axios from 'axios';
import Cookies from 'universal-cookie';
// Utils
import { v4 as uuidv4 } from 'uuid';
import { canUseFieldWrite, submitFieldWrite } from '../utils/offline/FieldWrite';
import { getOfflineServices } from '../utils/offline/OfflineServices';
import { isLocalId } from '../utils/offline/IdMap';
import { cleanupRemovedOps } from '../utils/offline/LocalCleanup';
import { isOnline } from '../utils/offline/Connectivity';

const BACKEND_ENDPOINT = AppSettings.getBackendUrl();

export default class FileInfosService {
    static getProjectFileInfos(projectId, type, projectFiles = false) {
        return Axios.get(`${BACKEND_ENDPOINT}fileInfos/?projectId=${projectId}&type=${type}&projectFiles=${projectFiles}`, {
            headers: { 'Authorization': new Cookies().get('token') ? `Bearer ${new Cookies().get('token')}` : '' }
        }).then(response => {
            return response.data;
        }, () => {
            if (isOnline()) {
                if (type === 'photo') showToast('photos_loading_failed');
                else showToast('files_loading_failed');
            }
            return;
        });
    }

    static addFileInfo(formData, axiosOptions = {}) {
        axiosOptions['headers'] = {
            'Authorization': `Bearer ${new Cookies().get('token')}`,
            'content-type': 'multipart/form-data'
        };

        return Axios.post(BACKEND_ENDPOINT + 'fileInfos/', formData, axiosOptions).then(response => {
            return response.data;
        }, () => {
            const isPhoto = formData.get('type') === 'photo';
            if (isOnline()) showToast(isPhoto ? 'photo_addition_failed' : 'file_addition_failed');
            return;
        });
    }

    // Photo compressée gardée sur l'appareil puis envoyée par la voie des photos, sans attendre l'API ; après la création de
    // son élément seulement si celle-ci est encore en file. Renvoie false si l'appareil est plein
    static async addPhotoThroughOutbox(tempPhoto, file) {
        const services = getOfflineServices();
        const photo = {
            id: tempPhoto.id, projectId: tempPhoto.projectId, elementId: tempPhoto.elementId, category: tempPhoto.category,
            recurrenceId: tempPhoto.recurrenceId ?? null, blobName: tempPhoto.blobName, createdAt: String(tempPhoto.originalCreatedAt), file
        };
        try {
            await services.photos.put(photo);
        } catch (error) {
            // Photo non gardée : toujours signalée, elle n'existe peut-être nulle part ailleurs (prise par l'appareil photo)
            console.error(error);
            showToast(error?.name === 'QuotaExceededError' || error?.inner?.name === 'QuotaExceededError' ? 'storage_full' : 'photo_addition_failed');
            return false;
        }
        // Élément toujours référencé : la file n'attend que sa création, simple ou en masse
        const refs = [
            ...(photo.elementId ? [{ entity: 'element', id: photo.elementId }] : []),
            ...(photo.recurrenceId < 0 ? [{ entity: 'occurrence', id: photo.recurrenceId }] : [])
        ];
        return submitFieldWrite({
            op: {
                type: 'photo.upload', lane: 'files', projectId: photo.projectId ? Number(photo.projectId) : null,
                target: { entity: 'photo', id: photo.id }, refs,
                // Photo d'une occurrence créée sur l'appareil : retirée avec elle, sinon avec l'élément
                cascadeKey: photo.recurrenceId < 0 ? `occurrence:${photo.recurrenceId}` : (photo.elementId ? `element:${photo.elementId}` : null),
                // Replantation de l'arbre en file : la photo part après, elle va au nouvel arbre
                softWaitFor: photo.elementId ? [`element:${photo.elementId}`] : [],
                payload: { photoId: photo.id }
            },
            revertLocal: services => services.photos.remove(photo.id)
        }).then(() => true, () => {
            showToast('photo_addition_failed');
            return false;
        });
    }

    // Une photo jamais envoyée est simplement retirée ; les autres sont supprimées par la file
    static async deletePhotosThroughOutbox(ids) {
        const services = getOfflineServices();
        const { cancelledKeys, removedOps } = await services.outbox.cancelLocalCreations(ids.filter(id => isLocalId('fileInfo', id)).map(id => `photo:${id}`));
        await cleanupRemovedOps(services, removedOps);
        const remaining = ids.filter(id => !cancelledKeys.includes(`photo:${id}`));
        const promise = !remaining.length ? Promise.resolve({ status: 200, data: null, isLocal: true }) : submitFieldWrite({
            op: {
                type: 'photo.delete', lane: 'files', projectId: null, target: { entity: 'photos', id: uuidv4() },
                touches: remaining.map(id => `photo:${id}`), refs: remaining.filter(id => isLocalId('fileInfo', id)).map(id => ({ entity: 'photo', id })),
                payload: { ids: remaining }
            },
            buildLocalData: () => null
        });
        return promise.then(response => {
            showToast(response.isLocal ? 'saved_on_device' : ids.length > 1 ? 'files_deleted' : 'file_deleted');
            return response;
        }, () => {
            showToast(ids.length > 1 ? 'files_deletion_failed' : 'file_deletion_failed');
            return;
        });
    }

    // Photo principale prise sur l'appareil : le choix part après son envoi
    static async updatePhotoPreviewThroughOutbox(category, elementId, blobName) {
        const services = getOfflineServices();
        const photoId = blobName?.split('.')[0];
        const isLocalPhoto = (await services.outbox.list()).some(op => op.type === 'photo.upload' && op.target.id === photoId);
        const refs = [{ entity: 'element', id: elementId }, ...(isLocalPhoto ? [{ entity: 'photo', id: photoId }] : [])];
        return submitFieldWrite({
            op: {
                type: 'photo.setPreview', lane: 'files', projectId: null, target: { entity: 'preview', id: elementId }, refs, payload: { category, elementId, blobName },
                // Photo de l'appareil supprimée avant son envoi : le choix est retiré avec elle
                cascadeKey: isLocalPhoto ? `photo:${photoId}` : null
            },
            buildLocalData: () => null
        }).then(response => {
            showToast(response.isLocal ? 'saved_on_device' : 'preview_updated');
            return response;
        }, () => {
            showToast('preview_update_failed');
            return;
        });
    }

    static updatePhotoThroughOutbox(fileInfo) {
        return submitFieldWrite({
            op: { type: 'photo.update', lane: 'files', projectId: fileInfo.projectId ?? null, target: { entity: 'photo', id: fileInfo.id }, payload: { fileInfo } },
            buildLocalData: () => fileInfo
        }).then(response => {
            showToast(response.isLocal ? 'saved_on_device' : 'file_updated');
            return response;
        }, () => {
            showToast('file_update_failed');
            return;
        });
    }

    static updateFileInfo(fileInfo) {
        if (fileInfo.type === 'photo' && canUseFieldWrite()) return FileInfosService.updatePhotoThroughOutbox(fileInfo);
        return Axios.put(BACKEND_ENDPOINT + 'fileInfos/' + fileInfo.id, fileInfo, {
            headers: {
                'Authorization': `Bearer ${new Cookies().get('token')}`
            }
        }).then(response => {
            if (isOnline()) showToast('file_updated');
            return response;
        }, () => {
            showToast('file_update_failed');
            return;
        });
    }

    // isPhoto : la galerie des fichiers reste en appel direct (rien hors-ligne pour les fichiers)
    static deleteFileInfos(ids, { isPhoto = false } = {}) {
        if (isPhoto && canUseFieldWrite()) return FileInfosService.deletePhotosThroughOutbox(ids);
        return Axios.delete(BACKEND_ENDPOINT + 'fileInfos/', {
            headers: {
                'Authorization': `Bearer ${new Cookies().get('token')}`
            },
            data: { ids }
        }).then(response => {
            if (isOnline() && ids.length === 1) showToast('file_deleted');
            else if (isOnline() && ids.length > 1) showToast('files_deleted');
            return response;
        }, () => {
            showToast(ids.length > 1 ? 'files_deletion_failed' : 'file_deletion_failed');
            return;
        });
    }

    static updatePhotoPreview(category, elementId, blobName) {
        if (canUseFieldWrite()) return FileInfosService.updatePhotoPreviewThroughOutbox(category, elementId, blobName);
        return Axios.put(BACKEND_ENDPOINT + 'fileInfos/photoPreview/', { category, elementId, blobName }, {
            headers: { 'Authorization': `Bearer ${new Cookies().get('token')}` }
        }).then(response => {
            if (isOnline()) showToast('preview_updated');
            return response;
        }, () => {
            showToast('preview_update_failed');
            return;
        });
    }

    static downloadPhotosZip(ids, label) {
        const promise = Axios.post(BACKEND_ENDPOINT + 'fileInfos/downloadPhotosZip', ids,
            {
                responseType: 'blob',
                headers: {
                    'Authorization': `Bearer ${new Cookies().get('token')}`,
                }
            });

        showLoadingToast('photos_downloading', 'photos_downloaded', 'photos_download_failed', promise);
        return promise
            .then(response => {
                if (!response?.data)
                    return;

                const blob = new Blob([response.data], { type: 'application/zip' });
                const fileUrl = window.URL.createObjectURL(blob);
                const tempLink = document.createElement('a');
                tempLink.href = fileUrl;
                const safeLabel = (label || 'photos').replace(/[^\w\-]+/g, '_');
                tempLink.setAttribute('download', `photos_${safeLabel}_${new Date().toISOString()}.zip`);
                document.body.appendChild(tempLink);
                tempLink.click();
                tempLink.remove();
                window.URL.revokeObjectURL(fileUrl);
            });
    }
}
